import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../components/AppIcon';
import { useTranslation } from 'react-i18next';

const AppMenuSection = ({ menuKey, menuIndex = 0 }) => {

  const { t } = useTranslation();

  const menuData = {
    home: {
      local: {
        title: t('appMenu.home.local.title'),
        subtitle: t('appMenu.home.local.subtitle'),
        description: t('appMenu.home.local.description'),
        points: t('appMenu.home.local.points', { returnObjects: true }),
        tip: t('appMenu.home.local.tip'),
      },
      global: {
        title: t('appMenu.home.global.title'),
        subtitle: t('appMenu.home.global.subtitle'),
        description: t('appMenu.home.global.description'),
        points: t('appMenu.home.global.points', { returnObjects: true }),
        tip: t('appMenu.home.global.tip'),
      }
    },
    mingl: {
      local: {
        title: t('appMenu.mingl.local.title'),
        subtitle: t('appMenu.mingl.local.subtitle'),
        description: t('appMenu.mingl.local.description'),
        points: t('appMenu.mingl.local.points', { returnObjects: true }),
        tip: t('appMenu.mingl.local.tip'),
      },
      global: {
        title: t('appMenu.mingl.global.title'),
        subtitle: t('appMenu.mingl.global.subtitle'),
        description: t('appMenu.mingl.global.description'),
        points: t('appMenu.mingl.global.points', { returnObjects: true }),
        tip: t('appMenu.mingl.global.tip'),
      }
    },
    connect: {
      local: {
        title: 'connect',
        subtitle: t('appMenu.connect.local.subtitle'),
        description: t('appMenu.connect.local.description'),
        points: t('appMenu.connect.local.points', { returnObjects: true }),
        tip: t('appMenu.connect.local.tip'),
      },
      global: {
        title: t('appMenu.connect.global.title'),
        subtitle: t('appMenu.connect.global.subtitle'),
        description: t('appMenu.connect.global.description'),
        points: t('appMenu.connect.global.points', { returnObjects: true }),
        tip: t('appMenu.connect.global.tip'),
      }
    },
    talk: {
      local: {
        title: t('appMenu.talk.local.title'),
        subtitle: t('appMenu.talk.local.subtitle'),
        description: t('appMenu.talk.local.description'),
        points: t('appMenu.talk.local.points', { returnObjects: true }),
        tip: t('appMenu.talk.local.tip'),
      },
      global: {
        title: t('appMenu.talk.global.title'),
        subtitle: t('appMenu.talk.global.subtitle'),
        description: t('appMenu.talk.global.description'),
        points: t('appMenu.talk.global.points', { returnObjects: true }),
        tip: t('appMenu.talk.global.tip'),
      }
    },
    passport: {
      local: {
        title: t('appMenu.passport.local.title'),
        subtitle: t('appMenu.passport.local.subtitle'),
        description: t('appMenu.passport.local.description'),
        points: t('appMenu.passport.local.points', { returnObjects: true }),
        tip: t('appMenu.passport.local.tip'),
      },
      global: {
        title: t('appMenu.passport.global.title'),
        subtitle: t('appMenu.passport.global.subtitle'),
        description: t('appMenu.passport.global.description'),
        points: t('appMenu.passport.global.points', { returnObjects: true }),
        tip: t('appMenu.passport.global.tip'),
      }
    }
  };

  // Get both local and global data
  const localData = menuData[menuKey].local;
  const globalData = menuData[menuKey].global;

  // Calculate background colors
  const isDark = menuIndex % 2 === 0;
  const localBg = isDark
    ? 'linear-gradient(135deg, rgba(255, 126, 95, 0.2) 0%, rgba(254, 180, 123, 0.2) 100%)'
    : 'rgba(255, 255, 255, 0.3)';
  const globalBg = isDark
    ? 'linear-gradient(135deg, rgba(56, 212, 204, 0.2) 0%, rgba(133, 229, 176, 0.2) 100%)'
    : 'rgba(255, 255, 255, 0.3)';

  // Render content for a single column
  const renderColumn = (data, isLocal) => (
    <div className="px-0 pt-10 pb-20">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="mb-8"
      >
      </motion.div>

      <motion.div
        className="transition-all duration-300"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        viewport={{ once: true }}
      >
        <div className="text-center mb-8 px-28">
          <h3 className="text-2xl font-bold text-gray-900 mb-2">
            {data.subtitle}
          </h3>
          <p className="text-base text-gray-600 mt-4 mb-6">
            <span style={{ color: '#6B7280' }}>
              {data.description}
            </span>
          </p>
        </div>

        <div className="flex justify-center mb-8">
          <div className="space-y-4 max-w-2xl">
            {data.points.map((point, index) => (
              <motion.div
                key={index}
                className="flex items-start space-x-3"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-gray-700">{point}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="flex justify-center">
          <motion.div
            className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl px-6 py-4 text-center inline-block"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            viewport={{ once: true }}
          >
            <p className="text-sm font-medium text-gray-800">
              💡 <strong>TIP</strong> {data.tip}
            </p>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );

  return (
    <div className="flex flex-col lg:flex-row">
      {/* Left Column - LOCAL */}
      <div className="w-full lg:w-1/2 transition-all duration-500" style={{ background: localBg }}>
        <div className="lg:pr-[160px] xl:pr-[180px]">
          {renderColumn(localData, true)}
        </div>
      </div>

      {/* Right Column - GLOBAL */}
      <div className="w-full lg:w-1/2 transition-all duration-500" style={{ background: globalBg }}>
        <div className="lg:pl-[160px] xl:pl-[180px]">
          {renderColumn(globalData, false)}
        </div>
      </div>
    </div>
  );
};

export default AppMenuSection;